import { useCallback, useSyncExternalStore } from 'react'

/**
 * Shared media queries, so breakpoints stay consistent with docs/DESIGN.md.
 * CSS cannot read custom properties inside `@media`, so CSS modules repeat the px values.
 */
export const MEDIA = {
  /** Phones: 4-column grid. */
  mobile: '(max-width: 767px)',
  /** Tablets: 6-column grid. */
  tablet: '(min-width: 768px) and (max-width: 1023px)',
  /** Desktop and up: 12-column grid. */
  desktop: '(min-width: 1024px)',
  /** Touch-first devices: no magnetic hover and no custom cursor. */
  coarsePointer: '(pointer: coarse)',
  /** Devices with real hover. */
  hover: '(hover: hover) and (pointer: fine)',
} as const

/**
 * Subscribes to a CSS media query. It is correct on the first render, with no flash.
 *
 * @example const isMobile = useMediaQuery(MEDIA.mobile)
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    },
    [query],
  )
  const getSnapshot = () => window.matchMedia(query).matches
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
